import React, { useState } from 'react';
import { cn } from '@/utils/cn';
import { TargetedImagePicker } from '@/components/media/TargetedImagePicker';
import { CroppedThumbnail } from '@/components/media/CroppedThumbnail';
import { pendingKey, type PendingTargeted } from '@/types/media';
import { PhotoIcon, VideoCameraIcon, RectangleStackIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ImageThumbnailWithActions } from '@/components/common/ImageThumbnailWithActions';

/**
 * Demand Gen Ad Creation Step
 *
 * Supports three Demand Gen ad types:
 * 1. Multi-Asset Ad (image-based) - Headlines, descriptions, images, business name
 * 2. Video Responsive Ad - YouTube videos with text overlays
 * 3. Carousel Ad - Multiple cards with individual images and headlines
 *
 * Per Google Ads API documentation:
 * - Multi-Asset: 1-5 headlines (40 chars), 1-5 descriptions (90 chars), business name (25 chars)
 * - Video: YouTube video IDs, 1-5 headlines, 1-5 long headlines, 1-5 descriptions
 * - Carousel: 2-10 cards, each with headline (40 chars), image, final URL
 */

export type DemandGenAdType = 'MULTI_ASSET' | 'VIDEO' | 'CAROUSEL';

export interface CarouselCard {
  headline: string;
  /** Pending crop for this card's image. Materialized at wizard submit. */
  pending: PendingTargeted | null;
  finalUrl: string;
}

export interface DemandGenAdFormData {
  adType: DemandGenAdType;
  // Common fields
  adName: string;  // Ad name visible in Google Ads console
  businessName: string;
  callToAction: string;
  finalUrl: string;
  // Multi-Asset specific
  headlines: string[];
  descriptions: string[];
  landscapePending: PendingTargeted[];
  squarePending: PendingTargeted[];
  portraitPending: PendingTargeted[];
  logoPending: PendingTargeted[];
  // Video specific
  videoIds: string[];
  longHeadlines: string[];
  // Carousel specific
  carouselCards: CarouselCard[];
  // Channel controls (set at ad group level)
  channelSettings?: {
    youtubeInStream: boolean;
    youtubeInFeed: boolean;
    youtubeShorts: boolean;
    discover: boolean;
    gmail: boolean;
    display: boolean;
    // maps: boolean;  // Not yet supported in Google Ads API v22/v23
  };
}

// `DEFAULT_DEMAND_GEN_AD_FORM_DATA` was extracted to `./demandGenAdFormDefaults.ts`
// because exporting a runtime value alongside the component broke Vite's
// fast-refresh (react-refresh/only-export-components). Import from there.

// Call to action options supported by Google Ads API for Demand Gen ads
// Note: Only these 12 values are supported for call_to_action_text field
// See: https://developers.google.com/google-ads/api/reference/data/localized-call-to-action
const CALL_TO_ACTION_OPTIONS = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'APPLY_NOW', label: 'Apply Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'SUBSCRIBE', label: 'Subscribe' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'BOOK_NOW', label: 'Book Now' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'SEE_MORE', label: 'See More' },
  { value: 'VISIT_SITE', label: 'Visit Site' },
  { value: 'INSTALL', label: 'Install' },
];

interface DemandGenAdCreationStepProps {
  formData: DemandGenAdFormData;
  onChangeFormData: (data: Partial<DemandGenAdFormData>) => void;
  errors?: {
    headlines?: string;
    descriptions?: string;
    longHeadlines?: string;
    landscapeImages?: string;
    squareImages?: string;
    logoImages?: string;
    businessName?: string;
    finalUrl?: string;
    videoIds?: string;
    carouselCards?: string;
  };
}

// Pending-crop preview (CSS-cropped, no GCS write yet).
interface PendingPreviewProps {
  pending: PendingTargeted;
  onRemove: () => void;
  aspectRatio?: string;
}

function PendingPreview({ pending, onRemove, aspectRatio }: PendingPreviewProps) {
  const getSize = () => {
    switch (aspectRatio) {
      case '1.91:1': return { width: 76, height: 40 };
      case '1:1': return { width: 64, height: 64 };
      case '4:5': return { width: 48, height: 60 };
      default: return { width: 64, height: 64 };
    }
  };
  return (
    <ImageThumbnailWithActions
      alt={pending.source.alt_text || pending.source.original_filename}
      size={getSize()}
      onRemove={onRemove}
      removeLabel="Remove image"
      confirmTitle="Remove Image"
      confirmMessage="Are you sure you want to remove this image?"
    >
      <CroppedThumbnail source={pending.source} cropBox={pending.cropBox} />
    </ImageThumbnailWithActions>
  );
}

export const DemandGenAdCreationStep: React.FC<DemandGenAdCreationStepProps> = ({
  formData,
  onChangeFormData,
  errors,
}) => {
  // Image picker state. Picker confirmation only updates form data with
  // PendingTargeted[] — Targeted MediaImages are NOT created until the
  // wizard's submit handler materializes them.
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerMode, setImagePickerMode] = useState<'landscape' | 'square' | 'portrait' | 'logo' | 'carousel'>('landscape');
  const [carouselCardIndex, setCarouselCardIndex] = useState<number | null>(null);

  // Ad type selection
  const handleAdTypeChange = (adType: DemandGenAdType) => {
    onChangeFormData({ adType });
  };

  // Text field handlers
  const handleHeadlineChange = (index: number, value: string) => {
    const updated = [...formData.headlines];
    updated[index] = value;
    onChangeFormData({ headlines: updated });
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updated = [...formData.descriptions];
    updated[index] = value;
    onChangeFormData({ descriptions: updated });
  };

  const handleLongHeadlineChange = (index: number, value: string) => {
    const updated = [...formData.longHeadlines];
    updated[index] = value;
    onChangeFormData({ longHeadlines: updated });
  };

  const handleVideoIdChange = (index: number, value: string) => {
    const updated = [...formData.videoIds];
    updated[index] = value;
    onChangeFormData({ videoIds: updated });
  };

  // Add/remove field handlers
  const addHeadline = () => {
    if (formData.headlines.length < 5) {
      onChangeFormData({ headlines: [...formData.headlines, ''] });
    }
  };

  const removeHeadline = (index: number) => {
    if (formData.headlines.length > 1) {
      onChangeFormData({ headlines: formData.headlines.filter((_, i) => i !== index) });
    }
  };

  const addDescription = () => {
    if (formData.descriptions.length < 5) {
      onChangeFormData({ descriptions: [...formData.descriptions, ''] });
    }
  };

  const removeDescription = (index: number) => {
    if (formData.descriptions.length > 1) {
      onChangeFormData({ descriptions: formData.descriptions.filter((_, i) => i !== index) });
    }
  };

  const addLongHeadline = () => {
    if (formData.longHeadlines.length < 5) {
      onChangeFormData({ longHeadlines: [...formData.longHeadlines, ''] });
    }
  };

  const removeLongHeadline = (index: number) => {
    if (formData.longHeadlines.length > 1) {
      onChangeFormData({ longHeadlines: formData.longHeadlines.filter((_, i) => i !== index) });
    }
  };

  const addVideoId = () => {
    if (formData.videoIds.length < 5) {
      onChangeFormData({ videoIds: [...formData.videoIds, ''] });
    }
  };

  const removeVideoId = (index: number) => {
    if (formData.videoIds.length > 1) {
      onChangeFormData({ videoIds: formData.videoIds.filter((_, i) => i !== index) });
    }
  };

  // Carousel card handlers
  const handleCarouselCardChange = (
    index: number,
    field: keyof CarouselCard,
    value: string | PendingTargeted | null,
  ) => {
    const updated = [...formData.carouselCards];
    updated[index] = { ...updated[index], [field]: value };
    onChangeFormData({ carouselCards: updated });
  };

  const addCarouselCard = () => {
    if (formData.carouselCards.length < 10) {
      onChangeFormData({
        carouselCards: [...formData.carouselCards, { headline: '', pending: null, finalUrl: '' }],
      });
    }
  };

  const removeCarouselCard = (index: number) => {
    if (formData.carouselCards.length > 2) {
      onChangeFormData({
        carouselCards: formData.carouselCards.filter((_, i) => i !== index),
      });
    }
  };

  // Image picker handlers
  const openImagePicker = (mode: typeof imagePickerMode, cardIndex?: number) => {
    setImagePickerMode(mode);
    if (cardIndex !== undefined) {
      setCarouselCardIndex(cardIndex);
    }
    setShowImagePicker(true);
  };

  const handleImageConfirm = (pending: PendingTargeted[]) => {
    if (imagePickerMode === 'carousel' && carouselCardIndex !== null) {
      // Carousel cards take 1 pending crop each.
      const updated = [...formData.carouselCards];
      updated[carouselCardIndex] = {
        ...updated[carouselCardIndex],
        pending: pending[0] ?? null,
      };
      onChangeFormData({ carouselCards: updated });
    } else {
      switch (imagePickerMode) {
        case 'landscape':
          onChangeFormData({ landscapePending: pending });
          break;
        case 'square':
          onChangeFormData({ squarePending: pending });
          break;
        case 'portrait':
          onChangeFormData({ portraitPending: pending });
          break;
        case 'logo':
          onChangeFormData({ logoPending: pending });
          break;
      }
    }
    setShowImagePicker(false);
    setCarouselCardIndex(null);
  };

  // Map UI mode → spec placement_id. The TargetedImagePicker resolves the
  // aspect ratio from the placement key.
  const placementForMode: Record<Exclude<typeof imagePickerMode, 'carousel'>, string> = {
    landscape: 'marketing_image',
    square: 'square_marketing_image',
    portrait: 'portrait_marketing_image',
    logo: 'logo',
  };

  const getPickerConfig = () => {
    if (imagePickerMode === 'carousel' && carouselCardIndex !== null) {
      return { maxSelection: 1, placementKey: 'google_ads.marketing_image' };
    }
    switch (imagePickerMode) {
      case 'landscape':
        return { maxSelection: 20, placementKey: `google_ads.${placementForMode.landscape}` };
      case 'square':
        return { maxSelection: 20, placementKey: `google_ads.${placementForMode.square}` };
      case 'portrait':
        return { maxSelection: 20, placementKey: `google_ads.${placementForMode.portrait}` };
      case 'logo':
        return { maxSelection: 5, placementKey: `google_ads.${placementForMode.logo}` };
      default:
        return { maxSelection: 1, placementKey: 'google_ads.square_marketing_image' };
    }
  };

  const pickerConfig = getPickerConfig();

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Your Demand Gen Ad</h3>
        <p className="text-sm text-gray-600">
          Create engaging ads that appear across YouTube, Discover, Gmail, and more.
        </p>
      </div>

      {/* Ad Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Ad Type <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => handleAdTypeChange('MULTI_ASSET')}
            className={cn(
              'p-4 border-2 rounded-lg text-center transition-all',
              formData.adType === 'MULTI_ASSET'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <PhotoIcon className="h-8 w-8 mx-auto mb-2 text-gray-600" />
            <span className="block text-sm font-medium text-gray-900">Multi-Asset</span>
            <p className="text-xs text-gray-500 mt-1">Images + text</p>
          </button>

          <button
            type="button"
            onClick={() => handleAdTypeChange('VIDEO')}
            className={cn(
              'p-4 border-2 rounded-lg text-center transition-all',
              formData.adType === 'VIDEO'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <VideoCameraIcon className="h-8 w-8 mx-auto mb-2 text-gray-600" />
            <span className="block text-sm font-medium text-gray-900">Video</span>
            <p className="text-xs text-gray-500 mt-1">YouTube videos</p>
          </button>

          <button
            type="button"
            onClick={() => handleAdTypeChange('CAROUSEL')}
            className={cn(
              'p-4 border-2 rounded-lg text-center transition-all',
              formData.adType === 'CAROUSEL'
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            )}
          >
            <RectangleStackIcon className="h-8 w-8 mx-auto mb-2 text-gray-600" />
            <span className="block text-sm font-medium text-gray-900">Carousel</span>
            <p className="text-xs text-gray-500 mt-1">Multiple cards</p>
          </button>
        </div>
      </div>

      {/* Common Fields: Ad Name, Business Name & Call to Action */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900">Business Details</h4>

        {/* Ad Name */}
        <div>
          <label htmlFor="ad-name" className="block text-sm font-medium text-gray-700 mb-2">
            Ad Name
          </label>
          <input
            type="text"
            id="ad-name"
            value={formData.adName}
            onChange={(e) => onChangeFormData({ adName: e.target.value })}
            placeholder="Enter a name to identify this ad"
            maxLength={255}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Choose a descriptive name for this ad. {formData.adName.length}/255 characters. Cannot be changed after creation.
          </p>
        </div>

        {/* Business Name */}
        <div>
          <label htmlFor="business-name" className="block text-sm font-medium text-gray-700 mb-2">
            Business Name <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              id="business-name"
              value={formData.businessName}
              onChange={(e) => onChangeFormData({ businessName: e.target.value })}
              placeholder="Your Company Name"
              maxLength={25}
              className={cn(
                'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                { 'border-red-500': errors?.businessName }
              )}
            />
            <span className="text-xs text-gray-500 self-center min-w-[40px]">
              {formData.businessName.length}/25
            </span>
          </div>
          {errors?.businessName && (
            <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>
          )}
        </div>

        {/* Call to Action */}
        <div>
          <label htmlFor="call-to-action" className="block text-sm font-medium text-gray-700 mb-2">
            Call to Action
          </label>
          <select
            id="call-to-action"
            value={formData.callToAction}
            onChange={(e) => onChangeFormData({ callToAction: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {CALL_TO_ACTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Final URL (for Multi-Asset and Video only, Carousel has per-card URLs) */}
        {formData.adType !== 'CAROUSEL' && (
          <div>
            <label htmlFor="final-url" className="block text-sm font-medium text-gray-700 mb-2">
              Final URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              id="final-url"
              value={formData.finalUrl}
              onChange={(e) => onChangeFormData({ finalUrl: e.target.value })}
              placeholder="https://www.example.com/landing-page"
              className={cn(
                'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                { 'border-red-500': errors?.finalUrl }
              )}
            />
            {errors?.finalUrl && <p className="mt-1 text-sm text-red-600">{errors.finalUrl}</p>}
          </div>
        )}
      </div>

      {/* Multi-Asset Ad Fields */}
      {formData.adType === 'MULTI_ASSET' && (
        <>
          {/* Images Section */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900">Images</h4>

            {/* Landscape Images (Required) */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Landscape Images (1.91:1) <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.landscapePending.length}/20 (min 1 required)
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.landscapePending.map((p) => (
                  <PendingPreview
                    key={pendingKey(p)}
                    pending={p}
                    aspectRatio="1.91:1"
                    onRemove={() => {
                      const k = pendingKey(p);
                      onChangeFormData({
                        landscapePending: formData.landscapePending.filter((q) => pendingKey(q) !== k)
                      });
                    }}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => openImagePicker('landscape')}
                  className="w-20 h-10 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <PhotoIcon className="w-5 h-5" />
                </button>
              </div>
              {errors?.landscapeImages && (
                <p className="text-sm text-red-600">{errors.landscapeImages}</p>
              )}
              <p className="text-xs text-gray-500">Recommended: 1200x628px, max 5MB</p>
            </div>

            {/* Square Images (Required) */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Square Images (1:1) <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.squarePending.length}/20 (min 1 required)
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.squarePending.map((p) => (
                  <PendingPreview
                    key={pendingKey(p)}
                    pending={p}
                    aspectRatio="1:1"
                    onRemove={() => {
                      const k = pendingKey(p);
                      onChangeFormData({
                        squarePending: formData.squarePending.filter((q) => pendingKey(q) !== k)
                      });
                    }}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => openImagePicker('square')}
                  className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <PhotoIcon className="w-5 h-5" />
                </button>
              </div>
              {errors?.squareImages && (
                <p className="text-sm text-red-600">{errors.squareImages}</p>
              )}
              <p className="text-xs text-gray-500">Recommended: 1200x1200px, max 5MB</p>
            </div>

            {/* Portrait Images (Optional) */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Portrait Images (4:5)
                </label>
                <span className="text-xs text-gray-500">
                  {formData.portraitPending.length}/20
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.portraitPending.map((p) => (
                  <PendingPreview
                    key={pendingKey(p)}
                    pending={p}
                    aspectRatio="4:5"
                    onRemove={() => {
                      const k = pendingKey(p);
                      onChangeFormData({
                        portraitPending: formData.portraitPending.filter((q) => pendingKey(q) !== k)
                      });
                    }}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => openImagePicker('portrait')}
                  className="w-12 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <PhotoIcon className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500">Optional, ideal for mobile placements</p>
            </div>

            {/* Logo Images */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Logo Images (1:1) <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.logoPending.length}/5 (min 1 required)
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.logoPending.map((p) => (
                  <PendingPreview
                    key={pendingKey(p)}
                    pending={p}
                    aspectRatio="1:1"
                    onRemove={() => {
                      const k = pendingKey(p);
                      onChangeFormData({
                        logoPending: formData.logoPending.filter((q) => pendingKey(q) !== k)
                      });
                    }}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => openImagePicker('logo')}
                  className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <PhotoIcon className="w-4 h-4" />
                </button>
              </div>
              {errors?.logoImages && (
                <p className="text-sm text-red-600">{errors.logoImages}</p>
              )}
              <p className="text-xs text-gray-500">1200x1200px recommended</p>
            </div>
          </div>

          {/* Text Assets Section */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900">Text Assets</h4>

            {/* Headlines */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Headlines <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.headlines.length}/5 (min 1 required)
                </span>
              </div>
              <div className="space-y-2">
                {formData.headlines.map((headline, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={headline}
                      onChange={(e) => handleHeadlineChange(index, e.target.value)}
                      placeholder={`Headline ${index + 1}`}
                      maxLength={40}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-500 self-center min-w-[40px]">
                      {headline.length}/40
                    </span>
                    {formData.headlines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeHeadline(index)}
                        className="text-red-600 hover:text-red-700 px-2"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors?.headlines && (
                <p className="mt-1 text-sm text-red-600">{errors.headlines}</p>
              )}
              {formData.headlines.length < 5 && (
                <button
                  type="button"
                  onClick={addHeadline}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add headline
                </button>
              )}
            </div>

            {/* Descriptions */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Descriptions <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.descriptions.length}/5 (min 1 required)
                </span>
              </div>
              <div className="space-y-2">
                {formData.descriptions.map((description, index) => (
                  <div key={index} className="flex gap-2">
                    <textarea
                      value={description}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      placeholder={`Description ${index + 1}`}
                      maxLength={90}
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-500 self-start mt-2 min-w-[40px]">
                      {description.length}/90
                    </span>
                    {formData.descriptions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDescription(index)}
                        className="text-red-600 hover:text-red-700 px-2 self-start mt-2"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors?.descriptions && (
                <p className="mt-1 text-sm text-red-600">{errors.descriptions}</p>
              )}
              {formData.descriptions.length < 5 && (
                <button
                  type="button"
                  onClick={addDescription}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add description
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Video Ad Fields */}
      {formData.adType === 'VIDEO' && (
        <>
          {/* Video IDs Section */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900">YouTube Videos</h4>

            {/* Video IDs */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  YouTube Video URLs <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.videoIds.filter(v => v.trim()).length}/5 (min 1 required)
                </span>
              </div>
              <div className="space-y-2">
                {formData.videoIds.map((videoId, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={videoId}
                      onChange={(e) => handleVideoIdChange(index, e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=... or video ID"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {formData.videoIds.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVideoId(index)}
                        className="text-red-600 hover:text-red-700 px-2"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors?.videoIds && (
                <p className="mt-1 text-sm text-red-600">{errors.videoIds}</p>
              )}
              {formData.videoIds.length < 5 && (
                <button
                  type="button"
                  onClick={addVideoId}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add video
                </button>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Enter YouTube video URLs or video IDs. Videos must be on your channel or public.
              </p>
            </div>

            {/* Logo Images for Video */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Logo Images (1:1) <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.logoPending.length}/5 (min 1 required)
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.logoPending.map((p) => (
                  <PendingPreview
                    key={pendingKey(p)}
                    pending={p}
                    aspectRatio="1:1"
                    onRemove={() => {
                      const k = pendingKey(p);
                      onChangeFormData({
                        logoPending: formData.logoPending.filter((q) => pendingKey(q) !== k)
                      });
                    }}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => openImagePicker('logo')}
                  className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                >
                  <PhotoIcon className="w-4 h-4" />
                </button>
              </div>
              {errors?.logoImages && (
                <p className="text-sm text-red-600">{errors.logoImages}</p>
              )}
            </div>
          </div>

          {/* Text Assets for Video */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900">Text Assets</h4>

            {/* Headlines */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Headlines <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.headlines.length}/5 (min 1 required)
                </span>
              </div>
              <div className="space-y-2">
                {formData.headlines.map((headline, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={headline}
                      onChange={(e) => handleHeadlineChange(index, e.target.value)}
                      placeholder={`Headline ${index + 1}`}
                      maxLength={40}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-500 self-center min-w-[40px]">
                      {headline.length}/40
                    </span>
                    {formData.headlines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeHeadline(index)}
                        className="text-red-600 hover:text-red-700 px-2"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors?.headlines && (
                <p className="mt-1 text-sm text-red-600">{errors.headlines}</p>
              )}
              {formData.headlines.length < 5 && (
                <button
                  type="button"
                  onClick={addHeadline}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add headline
                </button>
              )}
            </div>

            {/* Long Headlines */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Long Headlines <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.longHeadlines.length}/5 (min 1 required)
                </span>
              </div>
              <div className="space-y-2">
                {formData.longHeadlines.map((longHeadline, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={longHeadline}
                      onChange={(e) => handleLongHeadlineChange(index, e.target.value)}
                      placeholder={`Long headline ${index + 1}`}
                      maxLength={90}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-500 self-center min-w-[40px]">
                      {longHeadline.length}/90
                    </span>
                    {formData.longHeadlines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLongHeadline(index)}
                        className="text-red-600 hover:text-red-700 px-2"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors?.longHeadlines && (
                <p className="mt-1 text-sm text-red-600">{errors.longHeadlines}</p>
              )}
              {formData.longHeadlines.length < 5 && (
                <button
                  type="button"
                  onClick={addLongHeadline}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add long headline
                </button>
              )}
            </div>

            {/* Descriptions */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Descriptions <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">
                  {formData.descriptions.length}/5 (min 1 required)
                </span>
              </div>
              <div className="space-y-2">
                {formData.descriptions.map((description, index) => (
                  <div key={index} className="flex gap-2">
                    <textarea
                      value={description}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      placeholder={`Description ${index + 1}`}
                      maxLength={90}
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-500 self-start mt-2 min-w-[40px]">
                      {description.length}/90
                    </span>
                    {formData.descriptions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDescription(index)}
                        className="text-red-600 hover:text-red-700 px-2 self-start mt-2"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {errors?.descriptions && (
                <p className="mt-1 text-sm text-red-600">{errors.descriptions}</p>
              )}
              {formData.descriptions.length < 5 && (
                <button
                  type="button"
                  onClick={addDescription}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  + Add description
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Carousel Ad Fields */}
      {formData.adType === 'CAROUSEL' && (
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium text-gray-900">Carousel Cards</h4>
            <span className="text-xs text-gray-500">
              {formData.carouselCards.length}/10 cards (min 2 required)
            </span>
          </div>

          {errors?.carouselCards && (
            <p className="text-sm text-red-600">{errors.carouselCards}</p>
          )}

          <div className="space-y-4">
            {formData.carouselCards.map((card, index) => (
              <div
                key={index}
                className="p-4 border border-gray-200 rounded-lg bg-gray-50"
              >
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-700">Card {index + 1}</span>
                  {formData.carouselCards.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeCarouselCard(index)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Card Image */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Image (1.91:1) <span className="text-red-500">*</span>
                    </label>
                    {card.pending ? (
                      <PendingPreview
                        pending={card.pending}
                        aspectRatio="1.91:1"
                        onRemove={() => handleCarouselCardChange(index, 'pending', null)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => openImagePicker('carousel', index)}
                        className="w-full h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                      >
                        <PlusIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>

                  {/* Card Headline & URL */}
                  <div className="md:col-span-2 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Headline <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={card.headline}
                          onChange={(e) => handleCarouselCardChange(index, 'headline', e.target.value)}
                          placeholder="Card headline"
                          maxLength={40}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <span className="text-xs text-gray-500 self-center">
                          {card.headline.length}/40
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Final URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={card.finalUrl}
                        onChange={(e) => handleCarouselCardChange(index, 'finalUrl', e.target.value)}
                        placeholder="https://www.example.com/page"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {formData.carouselCards.length < 10 && (
            <button
              type="button"
              onClick={addCarouselCard}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              Add Card
            </button>
          )}
        </div>
      )}

      {/* Image Picker Modal — TargetedImagePicker shows the crop modal and
          returns `PendingTargeted[]` (in-memory source + placementKey + cropBox).
          Persistence is deferred: this surface materializes via the
          `useMaterializePendingTargeted` hook on submit, not at picker close.
          Carousel cards reuse the landscape (marketing_image) placement per
          Google's spec. */}
      <TargetedImagePicker
        isOpen={showImagePicker}
        onClose={() => {
          setShowImagePicker(false);
          setCarouselCardIndex(null);
        }}
        usageContext={{
          platform: 'google_ads',
          placementKey:
            imagePickerMode === 'carousel'
              ? 'google_ads.marketing_image'
              : `google_ads.${placementForMode[imagePickerMode]}`,
        }}
        onConfirm={handleImageConfirm}
        maxSelection={pickerConfig.maxSelection}
      />
    </div>
  );
};
